<template>
  <div class="mall-card-register">
    <div class="page">
      <MallHeader @back-click="goBack">
        <div slot="right"></div>
      </MallHeader>

      <div class="mall-card-register__content">
        <div class="mall-card-register__top">
          <Mall3dIcon :src="cardIcon" :size="52" :screenOpacity="0.4" />
          <div class="mall-card-register__title">
            <span class="mall-card-register__heading">결제 카드 등록</span>
            <span class="mall-card-register__desc">비대면 진료·소모성재료 결제를 위해<br />카드를 등록해 주세요.</span>
          </div>
        </div>

        <div class="mall-card-register__form">
          <div class="mall-card-register__card-number">
            <MallFieldLabel :label="'카드번호'" :required="true" />
            <div class="mall-card-register__card-number-area">
              <div class="mall-card-register__card-number-inputs">
                <!-- 앞 8자리만 보이게 (기존 카드 등록 화면과 동일) -->
                <MallTextField
                  v-for="(part, index) in form.cardNumber"
                  :key="index"
                  :ref="'cardNumber' + index"
                  class="mall-card-register__card-number-input"
                  :value="part"
                  :type="index < 2 ? 'text' : 'password'"
                  :inputmode="'numeric'"
                  :numeric="true"
                  :maxlength="4"
                  :placeholder="'0000'"
                  :invalid="!!errors.cardNumber"
                  @input="onCardNumberInput(index, $event)"
                  @keydown="onCardNumberKeydown(index, $event)"
                  @blur="onCardNumberBlur"
                />
              </div>
              <span v-if="errors.cardNumber" class="mall-card-register__error">{{ errors.cardNumber }}</span>
            </div>
          </div>
          <MallTextField
            v-model="form.expiry"
            :label="'유효기간'"
            :required="true"
            :inputmode="'numeric'"
            :numeric="true"
            :maxlength="4"
            :placeholder="'MMYY'"
            :error="errors.expiry"
            @input="untouch('expiry')"
            @blur="touch('expiry')"
          />
          <MallTextField
            v-model="form.password"
            :label="'비밀번호 앞 2자리'"
            :required="true"
            :type="'password'"
            :inputmode="'numeric'"
            :numeric="true"
            :maxlength="2"
            :placeholder="'**'"
            :error="errors.password"
            @input="untouch('password')"
            @blur="touch('password')"
          />
          <MallTextField
            v-model="form.userName"
            :label="'이름(카드 소유자)'"
            :required="true"
            :placeholder="'이름을 적어주세요'"
            :error="errors.userName"
            @input="untouch('userName')"
            @blur="touch('userName')"
          />
          <MallTextField
            v-model="form.nickName"
            :label="'카드 별명 (최대 5글자)'"
            :required="true"
            :maxlength="5"
            :placeholder="'별명을 입력해 주세요'"
            :error="errors.nickName"
            @input="untouch('nickName')"
            @blur="touch('nickName')"
          />
          <MallTextField
            v-model="form.birth"
            :label="'생년월일(사업자번호)'"
            :required="true"
            :inputmode="'numeric'"
            :numeric="true"
            :maxlength="10"
            :placeholder="'주민번호 앞 6자리 또는 사업자번호 10자리'"
            :error="errors.birth"
            @input="untouch('birth')"
            @blur="touch('birth')"
          />
        </div>
      </div>

      <div class="mall-card-register__divider"></div>

      <div class="mall-card-register__agree">
        <span class="mall-card-register__agree-title">{{ profileName || '고객' }}님께 필요한 동의만 추렸어요</span>
        <div class="mall-card-register__agree-item" @click="agreed = !agreed">
          <div class="mall-card-register__agree-check">
            <MallCheckBox :checked="agreed" />
            <span class="mall-card-register__agree-label">[필수] 개인정보 수집 및 이용 동의</span>
          </div>
          <img
            class="mall-card-register__agree-detail"
            src="@/assets/mall/icn_caret_right_light.svg"
            alt="약관 보기"
            @click.stop="openPolicy"
          />
        </div>
      </div>

      <MallBottomBar :buttons="buttons" @click="register" />

      <!-- 입력 중에 나가려고 할 때 -->
      <MallNoticeSheet
        v-if="isLeaveSheet"
        :title="'카드 등록을 취소할까요?'"
        :messageLines="['지금 나가면 입력한 내용이 저장되지 않아요.']"
        :cancelLabel="'나가기'"
        :confirmLabel="'계속 등록하기'"
        :dim="'normal'"
        @cancel="leave"
        @close="stay"
        @confirm="stay"
      />

      <!-- 카드 등록 실패 -->
      <MallCardErrorSheet
        v-if="errorMessage"
        :message="errorMessage"
        @confirm="errorMessage = ''"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import convert from '@/utilities/convert.js'
import mall from '@/utilities/mall.js'
import Mall3dIcon from '@/components/new/atoms/Mall3dIcon.vue'
import MallCheckBox from '@/components/new/atoms/MallCheckBox.vue'
import MallFieldLabel from '@/components/new/atoms/MallFieldLabel.vue'
import MallBottomBar from '@/components/new/molecules/MallBottomBar.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallTextField from '@/components/new/molecules/MallTextField.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallCardErrorSheet from '@/components/new/organisms/MallCardErrorSheet.vue'
import MallNoticeSheet from '@/components/new/organisms/MallNoticeSheet.vue'

var PRIVACY_POLICY_URL = 'https://policy.soldoc.co.kr/privacy-consent/payment'

// 결제 카드 등록·교체 (위임장 작성 흐름의 /card 화면과 별개)
export default {
  name: 'MallCardRegister',
  components: {
    Mall3dIcon,
    MallCheckBox,
    MallFieldLabel,
    MallBottomBar,
    MallHeader,
    MallTextField,
    MallToast,
    MallCardErrorSheet,
    MallNoticeSheet,
  },
  data() {
    return {
      cardIcon: require('@/assets/mall/img_card_3d.png'),
      profileId: Number(this.$route.query.profileId) || null,
      token: null,
      profileName: '',
      form: {
        cardNumber: ['', '', '', ''],
        expiry: '',
        password: '',
        userName: '',
        nickName: '',
        birth: '',
      },
      agreed: false,
      // 입력을 마친(포커스가 빠진) 칸 - 잘못 입력한 경우에만 오류 문구 노출
      touched: {
        cardNumber: false,
        expiry: false,
        password: false,
        userName: false,
        nickName: false,
        birth: false,
      },
      // 입력이 덜 된 상태로 등록 버튼을 누르면 빈 칸까지 모두 오류 노출
      isSubmitted: false,
      isRequesting: false,
      // 나가기 확인 시트 / 보류 중인 화면 이동 (beforeRouteLeave의 next)
      isLeaveSheet: false,
      pendingLeave: null,
      // 등록을 마쳤거나 로그인으로 보낼 때는 확인 없이 이동
      canLeave: false,
      errorMessage: '',
      toast: null,
    }
  },
  computed: {
    isDirty() {
      var form = this.form
      return this.agreed || form.cardNumber.join('') !== '' || !!(form.expiry || form.password || form.userName || form.nickName || form.birth)
    },
    // 칸별 오류 문구 (문제가 없으면 '')
    fieldErrors() {
      var form = this.form
      var name = form.userName.trim()
      // 마지막 자리는 15자리 카드를 위해 3자리부터 허용 (기존 카드 등록 화면과 동일)
      var isCardNumber = form.cardNumber.every((part, index) => index < 3 ? part.length === 4 : part.length >= 3)
      return {
        cardNumber: isCardNumber ? '' : '카드번호를 모두 입력해 주세요.',
        expiry: form.expiry === '' ? '유효기간을 입력해 주세요.' : /^(0[1-9]|1[0-2])[0-9]{2}$/.test(form.expiry) ? '' : '월·연도 순서(MMYY)로 입력해 주세요.',
        password: form.password.length === 2 ? '' : '비밀번호 앞 2자리를 입력해 주세요.',
        userName: name === '' ? '이름을 입력해 주세요.' : convert.containsNumber(name) ? '이름은 한글 또는 영문으로 입력해 주세요.' : '',
        nickName: form.nickName.trim() === '' ? '카드 별명을 입력해 주세요.' : '',
        birth: this.isValidBirth(form.birth) ? '' : '주민번호 앞 6자리 또는 사업자번호 10자리를 입력해 주세요.',
      }
    },
    // 화면에 보이는 오류 - 입력을 마친 칸 중 값이 있는 칸만 (등록 버튼을 누른 뒤에는 빈 칸도)
    errors() {
      var errors = {}
      Object.keys(this.fieldErrors).forEach((key) => {
        var value = key === 'cardNumber' ? this.form.cardNumber.join('') : this.form[key]
        var isVisible = this.isSubmitted || (this.touched[key] && value !== '')
        errors[key] = isVisible ? this.fieldErrors[key] : ''
      })
      return errors
    },
    isValid() {
      var fieldErrors = this.fieldErrors
      return Object.keys(fieldErrors).every((key) => fieldErrors[key] === '') && this.agreed
    },
    // 입력이 덜 됐어도 누를 수 있게 두고(비활성 모양) 누르면 이유를 안내
    buttons() {
      return [{ key: 'register', label: '카드 등록하기', type: 'primary', dimmed: !this.isValid, disabled: this.isRequesting }]
    },
  },
  mounted() {
    ChannelIO('shutdown')
    this.token = mall.getAccessToken()
    if (!this.token) {
      this.goLogin()
      return
    }
    // 동의 영역의 이름 표시용 (실패해도 등록은 가능)
    mall.fetchProfile(this.$store, this.profileId, this.token)
      .then((profile) => {
        this.profileName = profile ? profile.profile.name : ''
      })
      .catch((err) => {
        if (mall.isAuthError(err)) this.goLogin()
      })
  },
  // 입력한 내용이 있으면 뒤로가기(헤더·기기 버튼 모두)를 멈추고 확인 시트를 띄움
  beforeRouteLeave(to, from, next) {
    if (this.canLeave || !this.isDirty) {
      next()
      return
    }
    if (this.pendingLeave) this.pendingLeave(false)
    this.pendingLeave = next
    this.isLeaveSheet = true
  },
  methods: {
    // 헤더 뒤로가기는 화면 이동 전에 확인 시트를 띄움 (기기 뒤로가기는 beforeRouteLeave에서 처리)
    goBack() {
      if (!this.canLeave && this.isDirty) {
        this.isLeaveSheet = true
        return
      }
      this.moveBack()
    },
    // 카드 관리로 (관리 화면의 returnTo 유지)
    moveBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/card', query: { profileId: this.profileId } })
    },
    goLogin() {
      this.canLeave = true
      mall.goLogin(this.$router)
    },
    leave() {
      var next = this.pendingLeave
      this.pendingLeave = null
      this.isLeaveSheet = false
      this.canLeave = true
      if (next) {
        next()
      } else {
        this.moveBack()
      }
    },
    stay() {
      var next = this.pendingLeave
      this.pendingLeave = null
      this.isLeaveSheet = false
      if (next) next(false)
    },
    onCardNumberInput(index, value) {
      this.untouch('cardNumber')
      this.$set(this.form.cardNumber, index, value)
      if (value.length === 4 && index < 3) {
        this.focusCardNumber(index + 1)
      }
    },
    // 빈 칸에서 지우기를 누르면 앞 칸으로
    onCardNumberKeydown(index, event) {
      if (event.key === 'Backspace' && this.form.cardNumber[index] === '' && index > 0) {
        this.focusCardNumber(index - 1)
      }
    },
    focusCardNumber(index) {
      var field = this.$refs['cardNumber' + index]
      if (field && field[0]) field[0].focus()
    },
    // 카드번호 칸끼리 이동할 때는 입력 중으로 보고, 다른 곳으로 나갈 때만 입력을 마친 것으로 봄
    onCardNumberBlur(event) {
      var next = event.relatedTarget
      if (next && next.closest && next.closest('.mall-card-register__card-number-inputs')) return
      this.touch('cardNumber')
    },
    touch(key) {
      this.touched[key] = true
    },
    // 다시 입력하는 동안에는 오류를 숨겼다가 칸을 나가면 다시 확인
    untouch(key) {
      this.touched[key] = false
    },
    // 입력이 덜 된 채로 등록을 누르면: 동의만 남았으면 안내, 아니면 첫 오류 칸으로 스크롤
    showInvalidReason() {
      this.isSubmitted = true
      var fieldErrors = this.fieldErrors
      var hasFieldError = Object.keys(fieldErrors).some((key) => fieldErrors[key] !== '')
      if (!hasFieldError) {
        this.toast = { key: Date.now(), title: '개인정보 수집 및 이용에 동의해 주세요.', message: '' }
        return
      }
      this.$nextTick(() => {
        var target = this.$el.querySelector('.mall-text-field__input--error')
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    // 주민번호 앞 6자리(YYMMDD) 또는 사업자번호 10자리 (기존 카드 등록 화면과 동일)
    isValidBirth(value) {
      if (value.length === 6) return convert.isValidBirthdate(value)
      return value.length === 10
    },
    openPolicy() {
      window.open(PRIVACY_POLICY_URL, '_blank')
    },
    register() {
      if (this.isRequesting) return
      if (!this.isValid) {
        this.showInvalidReason()
        return
      }
      var form = this.form
      var card = {
        card_number: form.cardNumber.join(''),
        card_pw: form.password,
        card_ex_month: form.expiry.slice(0, 2),
        card_ex_year: form.expiry.slice(2, 4),
        card_nick_name: form.nickName.trim(),
        card_user_name: form.userName.trim(),
        card_user_birth: form.birth,
      }
      this.isRequesting = true
      this.$store.dispatch(Constant.SAVE_PROFILE_CARD, { card: card, profile_id: this.profileId, token: this.token })
        .then(() => {
          this.canLeave = true
          this.moveBack()
        })
        .catch((err) => {
          this.isRequesting = false
          if (mall.isAuthError(err)) {
            this.goLogin()
            return
          }
          // 서버가 사유를 준 오류(카드 정보 오류 등)는 시트로, 그 밖에는 토스트로
          if (err && err.code) {
            this.errorMessage = mall.errorMessage(err)
            return
          }
          this.toast = { key: Date.now(), title: '카드를 등록하지 못했어요.', message: mall.errorMessage(err) }
        })
    },
  },
}
</script>

<style scoped>
  .mall-card-register {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  /* 하단 고정 버튼 영역(90px)에 내용이 가려지지 않도록 */
  .page {
    width: 100%;
    min-height: 100vh;
    padding-bottom: 90px;
    background: #FFF;
    box-sizing: border-box;
  }
  .mall-card-register__content {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 48px;
  }
  .mall-card-register__top {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .mall-card-register__title {
    display: flex;
    padding-left: 4px;
    flex-direction: column;
    gap: 4px;
  }
  .mall-card-register__heading {
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-tight);
  }
  .mall-card-register__desc {
    color: #6B7684;
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
  }
  .mall-card-register__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  .mall-card-register__card-number {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .mall-card-register__card-number-area {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .mall-card-register__card-number-inputs {
    display: flex;
    gap: 4px;
  }
  /* MallTextField 오류 문구와 같은 모양 */
  .mall-card-register__error {
    padding: 0 4px;
    color: #F04452;
    font-family: Pretendard;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    line-height: 17px;
  }
  .mall-card-register__card-number-input {
    flex: 1;
    min-width: 0;
  }
  .mall-card-register__divider {
    height: 12px;
    background: #F2F4F6;
  }
  .mall-card-register__agree {
    display: flex;
    padding: 32px 20px;
    flex-direction: column;
    gap: 8px;
  }
  .mall-card-register__agree-title {
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-normal);
  }
  .mall-card-register__agree-item {
    display: flex;
    padding: 16px 8px;
    align-items: center;
    gap: 4px;
    border-radius: 12px;
    background: #F2F4F6;
    cursor: pointer;
  }
  .mall-card-register__agree-check {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 12px;
  }
  .mall-card-register__agree-label {
    flex: 1;
    min-width: 0;
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
  }
  .mall-card-register__agree-detail {
    display: block;
    flex-shrink: 0;
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallCardRegister.vue